import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, type RouteObject } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout, type RouteHandle } from './components/Layout';
import { Loading, ToastProvider } from './components/ui';
import { useSettings, useUpdateSettings, useUsage } from './api/hooks';
import { useMachine } from './machine/store';
import { useScale } from './scale/store';
import { ApiRequestError } from './api/client';
import { installChunkReloadHandler } from './lib/chunkReload';
import { SettingsUnitsProvider } from './lib/units';
import { setUsageSharing } from './lib/usage';
// Static, not lazy: the error screens must render when page chunks can't be loaded.
import { RootErrorPage, RouteErrorPage } from './pages/RouteErrorPage';
import { AuthGate } from './auth/AuthGate';
import type { UsagePage } from '@shared/usage';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const BrewWizardPage = lazy(() => import('./pages/BrewWizardPage'));
const BrewsPage = lazy(() => import('./pages/BrewsPage'));
const BrewDetailPage = lazy(() => import('./pages/BrewDetailPage'));
const BrewComparePage = lazy(() => import('./pages/BrewComparePage'));
const BeansPage = lazy(() => import('./pages/BeansPage'));
const BeanDetailPage = lazy(() => import('./pages/BeanDetailPage'));
const BeanFormPage = lazy(() => import('./pages/BeanFormPage'));
const EquipmentPage = lazy(() => import('./pages/EquipmentPage'));
const MethodPage = lazy(() => import('./pages/MethodPage'));
const ScalePage = lazy(() => import('./pages/ScalePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AskPage = lazy(() => import('./pages/AskPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      // A 4xx (e.g. a deleted brew) won't fix itself: show "not found" right away instead of after a retry.
      retry: (failures, error) => failures < 1 && !(error instanceof ApiRequestError && error.status < 500),
      refetchOnWindowFocus: true,
    },
  },
});

function page(el: React.ReactNode) {
  return <Suspense fallback={<Loading />}>{el}</Suspense>;
}

/** `handle.title` names the browser tab ("History · Taretto"); detail pages put a bean's or method's name instead. */
const titled = (title: string): RouteHandle => ({ title });

/** Each page's handle also names it for usage events (page_viewed), by its path: /beans/:id, never an id. */
const withPages = (pages: Array<RouteObject & { path: UsagePage }>): RouteObject[] =>
  pages.map((r) => ({ ...r, handle: { ...(r.handle as RouteHandle), page: r.path } satisfies RouteHandle }));

/**
 * Routes:
 *   /                     dashboard
 *   /brew                 guided smart brew wizard (?from=<brewId> repeats a brew, ?beanId=<id> preselects a bean)
 *   /brews                brew history         /brews/:id  brew detail (edit/delete)
 *   /brews/compare?ids=1,2,3                   overlay graphs
 *   /beans                bean list            /beans/new  /beans/:id  /beans/:id/edit
 *   /equipment            grinders / vessels / methods (?tab=grinders|vessels|methods)
 *   /equipment/methods/new (?from=<id> copies a method)   /equipment/methods/:id  brew method and its workflow
 *   /scale                live scale view, manual tare/timer, simulator controls (dev builds only), packet log
 *   /settings             workflow tuning, backup/restore, HTTPS help
 *   /ask                  Ask Taretto, a chat about your own data (?brew=<id> or ?bean=<id> asks about that one)
 */
export const routes: RouteObject[] = [
  {
    id: 'root',
    element: <Layout />,
    // Only reached when Layout itself fails.
    errorElement: <RootErrorPage />,
    children: [
      {
        // Pathless boundary for every page: a page that fails to load (stale chunk after a deploy) or throws
        // while rendering shows an error with Reload inside Layout's <Outlet>, keeping the nav.
        id: 'pages',
        errorElement: <RouteErrorPage />,
        children: withPages([
          { path: '/', element: page(<DashboardPage />), handle: titled('Home') },
          { path: '/brew', element: page(<BrewWizardPage />), handle: titled('Brew') },
          { path: '/brews', element: page(<BrewsPage />), handle: titled('History') },
          { path: '/brews/compare', element: page(<BrewComparePage />), handle: titled('Compare brews') },
          { path: '/brews/:id', element: page(<BrewDetailPage />), handle: titled('Brew details') },
          { path: '/beans', element: page(<BeansPage />), handle: titled('Beans') },
          { path: '/beans/new', element: page(<BeanFormPage />), handle: titled('Add beans') },
          { path: '/beans/:id', element: page(<BeanDetailPage />), handle: titled('Beans') },
          { path: '/beans/:id/edit', element: page(<BeanFormPage />), handle: titled('Edit beans') },
          { path: '/equipment', element: page(<EquipmentPage />), handle: titled('Gear') },
          { path: '/equipment/methods/new', element: page(<MethodPage />), handle: titled('New method') },
          { path: '/equipment/methods/:id', element: page(<MethodPage />), handle: titled('Method') },
          { path: '/scale', element: page(<ScalePage />), handle: titled('Scale') },
          { path: '/settings', element: page(<SettingsPage />), handle: titled('Settings') },
          { path: '/ask', element: page(<AskPage />), handle: titled('Ask Taretto') },
          { path: '*', element: page(<NotFoundPage />), handle: titled('Page not found') },
        ]),
      },
    ],
  },
];

// Created on first render rather than at import, so the route table can be imported outside a browser (tests).
let browserRouter: ReturnType<typeof createBrowserRouter> | null = null;
function getRouter() {
  return (browserRouter ??= createBrowserRouter(routes));
}

// A chunk that fails to preload after a deploy triggers one automatic reload (see lib/chunkReload).
installChunkReloadHandler();

/**
 * Keeps the scale store in sync with server-side settings and attempts BLE auto-reconnect once, for the scale and the
 * coffee machine. Also gives the
 * server this device's time zone the first time, so it counts days the way the user does (hosted runs in UTC).
 */
function SettingsSync() {
  const { data: settings } = useSettings();
  const { mutate: updateSettings } = useUpdateSettings();
  const setStabilityOptions = useScale((s) => s.setStabilityOptions);
  const setAutoReconnect = useScale((s) => s.setAutoReconnect);
  const setKeepAwake = useScale((s) => s.setKeepAwake);
  const tryAutoConnect = useScale((s) => s.tryAutoConnect);

  useEffect(() => {
    if (!settings) return;
    setStabilityOptions({ windowMs: settings.stabilityWindowMs, toleranceG: settings.stabilityToleranceG });
    setAutoReconnect(settings.autoReconnectScale);
    setKeepAwake(settings.keepAwakeScale);
  }, [settings, setStabilityOptions, setAutoReconnect, setKeepAwake]);

  const needsTimeZone = settings != null && settings.timeZone == null;
  useEffect(() => {
    if (needsTimeZone) updateSettings({ timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
  }, [needsTimeZone, updateSettings]);

  const autoReconnect = settings?.autoReconnectScale;
  useEffect(() => {
    if (autoReconnect) void tryAutoConnect();
  }, [autoReconnect, tryAutoConnect]);

  // The coffee machine connected last, if there is one: Disconnect is how to stop it.
  useEffect(() => {
    void useMachine.getState().tryAutoConnect();
  }, []);

  return null;
}

/** Sends usage events only while this account shares them (lib/usage.ts), and stops when it no longer does. */
function UsageSync() {
  const usage = useUsage().data;
  const sharing = usage != null && usage.collecting && usage.share;
  useEffect(() => setUsageSharing(sharing), [sharing]);
  return null;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthGate>
          <SettingsSync />
          <UsageSync />
          <SettingsUnitsProvider>
            <RouterProvider router={getRouter()} />
          </SettingsUnitsProvider>
        </AuthGate>
      </ToastProvider>
    </QueryClientProvider>
  );
}
